import React, { useEffect } from "react";
import LiveChat from "../../pages/Chat/LiveChat";
import { enqueueSnackbar } from "notistack";
import { picBaseUrl } from "../../config/config";
import { useAdminContext } from "../../Hooks/AdminContext";
import moment from "moment";
import { useUserData } from "../../Hooks/App_Context";

export const ChatMain = ({
  open,
  handleClose,
  selectChat,
  setSelectChat,
  sendMessagesLoader,
  setSendMessagesLoader,
  delLoading,
  setDelLoading,
  tabValue,
  setChatList,
  setUserList,
  setEndChat,
  setChats,
  userList,
  chatList,
  setTabValue,
  setOpenDelete,
  openDelete,
  isChatInitiated,
  setIsChatInitiated,
  isChatSelected,
  setIsChatSelected,
  setMessageId,
  messageId,
  selectChatRef,
  classes,
  handleOverlayClick,
}) => {
  const { socket } = useAdminContext();
  const { setUnreadMessageCount, unreadMessageCount, setNewNotification } =
    useUserData();

  /**
   * Formats a raw chat object for display in the UI.
   * Extracts and transforms relevant participant and message info,
   * and provides fallbacks for missing data.
   */
  const formatChat = (chat) => {
    // Get the other participant (not the current user)
    const otherParticipant = chat?.participant?.find((p) => p.type !== 0);

    // Get current user's data for unread count
    const forCount = chat?.participant?.find((p) => p.type === 0);

    return {
      ...chat,
      id: chat._id,
      receiver_id: chat?.otherParticipant?.receiver_id,
      sender_id: chat?.otherParticipant?.sender_id,
      is_online: otherParticipant?.is_online,
      user_id: otherParticipant?.user_id,
      email: otherParticipant?.email || "Unknown",
      name: `${otherParticipant?.first_name || "Unknown"} ${
        otherParticipant?.last_name || ""
      }`,
      image: otherParticipant?.profile_image
        ? picBaseUrl + otherParticipant.profile_image
        : "https://www.w3schools.com/howto/img_avatar.png",
      time: chat.chat_date_time
        ? moment(chat.chat_date_time).format("DD-MM-YYYY hh:mm A")
        : "N/A",
      lastMessage: chat.last_message?.message_content || "No messages yet",
      lastMessageTime: moment(chat?.last_message?.message_date_time).format(
        "DD-MM-YYYY hh:mm A"
      ),
      unread: forCount?.unread_message_count > 0, // Show unread indicator if count > 0
    };
  };

  /**
   * Handles incoming chat messages received via socket.
   * Updates the chat state and UI with the new message or shows an error.
   */
  const handleIncomingMessage = (message) => {
    if (message.code === 200) {
      setSendMessagesLoader(false); // Stop the message loader
      setMessageId(null); // Clear the temporary message ID (used while sending)

      // Update the selected chat with the new message
      setSelectChat((prev) => {
        const newMessage = {
          _id: message?.message_add?._id,
          message_content: message?.message_add?.message_content,
          sender_id: message?.message_add?.sender_id,
          message_date_time: message?.message_add?.message_date_time,
        };

        return {
          ...prev,
          _id: message.chat_id, // Update chat ID just in case
          messages: [...(prev?.messages || []), newMessage], // Append the new message
        };
      });
    } else {
      setSendMessagesLoader(false); // Stop the loader on error
      setMessageId(null); // Clear the message ID
      enqueueSnackbar(message.message, {
        variant: "error", // Show error notification
      });
    }
  };

  // Handles ending a chat from the sender side and updates chat state accordingly
  const handleEndChatSender = (data) => {
    if (data?.code === 200) {
      setSelectChat((prev) => {
        return {
          ...prev,
          is_end: true,
        };
      });
      enqueueSnackbar(data?.message, {
        variant: "success",
      });
    } else {
      enqueueSnackbar(data?.message, {
        variant: "error",
      });
    }
  };

  // Handles chat end event for the receiver side
  // - Removes the chat from the chat list
  // - Updates unread message count
  // - Resets chat selection and tab state
  // - Updates chat as ended if successful, and shows snackbar notification
  const handleEndChatReceiver = (data) => {
    setChatList((prev) => prev.filter((chat) => chat._id !== data.chat_id));
    setUnreadMessageCount(data.unread_message_count);
    setIsChatSelected(false);
    setTabValue(0);

    if (data?.code === 200) {
      setSelectChat((prev) => {
        return {
          ...prev,
          is_end: true,
        };
      });
      enqueueSnackbar(data?.message, {
        variant: "success",
      });
    } else {
      enqueueSnackbar(data?.message, {
        variant: "error",
      });
    }
  };

  /**
   * Handles message update logic on the sender side.
   * Updates the message content in the selected chat if there's a change.
   * Displays a success or error toast accordingly.
   */
  const handleUpdateMessageSender = (message, sender = true) => {
    // Clear message ID used for tracking edited message
    setMessageId(null);

    if (message.code === 200) {
      setSelectChat((prev) => {
        // Map through messages to update content if ID matches and content has changed
        const updatedMessages = prev?.messages?.map((msg) => {
          if (
            msg?._id === message?.message_update?._id &&
            msg?.message_content !== message?.message_update?.message_content
          ) {
            return {
              ...msg,
              message_content: message?.message_update?.message_content,
            };
          }
          return msg;
        });

        // Check if any message has actually been updated
        const isMessagesUpdated = updatedMessages?.some(
          (msg, index) => msg !== prev.messages[index]
        );

        // If changes detected, update messages in selected chat
        if (isMessagesUpdated) {
          return {
            ...prev,
            messages: updatedMessages,
          };
        }

        return prev; // No change needed
      });

      // Show success toast if user is the sender
      if (sender) {
        enqueueSnackbar(message?.message || "Message updated successfully", {
          variant: "success",
        });
      }

      setSendMessagesLoader(false); // Stop loading spinner
    } else {
      setSendMessagesLoader(false);
      // Show error toast on failure
      enqueueSnackbar(message?.message, {
        variant: "error",
      });
    }
  };

  /**
   * Handles message update logic on the receiver side.
   * Updates the message content in the selected chat if the content has changed.
   * Displays a toast notification if the update is successful or fails.
   *
   * @param {Object} message - The updated message object from the server.
   * @param {boolean} sender - Indicates if the current user is the sender (default: false).
   */
  const handleUpdateMessageReceiver = (message, sender = false) => {
    if (message.code === 200) {
      setSelectChat((prev) => {
        // Iterate over messages to find and update the matching message content
        const updatedMessages = prev?.messages?.map((msg) => {
          if (
            msg?._id === message?.message_update?._id &&
            msg?.message_content !== message?.message_update?.message_content
          ) {
            return {
              ...msg,
              message_content: message?.message_update?.message_content,
            };
          }
          return msg;
        });

        // Check if any message content was actually changed
        const isMessagesUpdated = updatedMessages?.some(
          (msg, index) => msg !== prev.messages[index]
        );

        // Return updated chat object if changes were made
        if (isMessagesUpdated) {
          return {
            ...prev,
            messages: updatedMessages,
          };
        }

        return prev; // No update necessary
      });

      // Show success notification only if sender flag is true
      if (sender) {
        enqueueSnackbar(message?.message || "Message updated successfully", {
          variant: "success",
        });
      }

      setSendMessagesLoader(false); // Stop loading indicator
    } else {
      setSendMessagesLoader(false);
      // Show error notification if update failed
      enqueueSnackbar(message?.message, {
        variant: "error",
      });
    }
  };

  /**
   * Handles the deletion of a message for the receiver.
   * Updates the selected chat messages by removing the deleted one.
   * Also clears related notifications and updates UI states.
   *
   * @param {Object} message - The message data received from the server.
   * @param {boolean} sender - Whether the action was initiated by the sender (default: false).
   */
  const handleDeleteMessageReceiver = (message, sender = false) => {
    setNewNotification((prev) =>
      prev.filter((n) => n._id !== message.notification_id)
    );

    if (message.code === 200) {
      setSelectChat((prev) => {
        if (!prev || !prev.messages) return prev;
        const updatedMessages = prev.messages.filter(
          (msg) => msg?._id !== message?.message_delete?._id
        );
        return {
          ...prev,
          messages: updatedMessages,
        };
      });
      setOpenDelete(false);
      setDelLoading(false);
      if (sender) {
        enqueueSnackbar(message.message, {
          variant: "success",
        });
      }
    } else {
      setDelLoading(false);
      enqueueSnackbar(message.message, {
        variant: "error",
      });
    }
  };

  /**
   * Handles the deletion of a message for the sender.
   * Removes the deleted message from the selected chat and updates relevant UI states.
   *
   * @param {Object} message - The message data received from the server.
   * @param {boolean} sender - Indicates if the deletion was triggered by the sender (default: true).
   */
  const handleDeleteMessageSender = (message, sender = true) => {
    if (message.code === 200) {
      setSelectChat((prev) => {
        if (!prev || !prev.messages) return prev;
        const updatedMessages = prev.messages.filter(
          (msg) => msg?._id !== message?.message_delete?._id
        );
        return {
          ...prev,
          messages: updatedMessages,
        };
      });
      setOpenDelete(false);
      setDelLoading(false);
      if (sender) {
        enqueueSnackbar(message.message, {
          variant: "success",
        });
      }
    } else {
      setDelLoading(false);
      enqueueSnackbar(message.message, {
        variant: "error",
      });
    }
  };

  /**
   * Updates the online status of a specific user in the chat list.
   * @param {Array} chatList - The current list of chat users.
   * @param {string} userId - The ID of the user whose status is to be updated.
   * @param {boolean} isOnline - The new online status to set.
   * @returns {Array} Updated chat list with the user's online status changed.
   */
  const updateUserOnlineStatus = (chatList, userId, isOnline) => {
    return chatList.map((user) =>
      user.user_id === userId ? { ...user, is_online: isOnline } : user
    );
  };

  /**
   * Handles the sender's event for a new chat message.
   * This function is triggered when a new message is sent by the user.
   * It processes the incoming message and resets the message ID state.
   */
  const handleSenderEvent = (data) => {
    // console.log("🔥 Event hit: send_chat_message_event_for_sender", data);
    handleIncomingMessage(data);
    setMessageId(null);
  };

  /**
   * Handles incoming chat messages for the receiver.
   * This function updates the chat list with the new message or chat,
   * adds the notification, and updates unread message count if the
   * user is not currently viewing the chat.
   */
  const handleReceiverEvent = (data) => {
    // console.log("🔥 Event hit: send_chat_message_event_for_Reciver", data);

    setChatList((prev) => {
      if (data?.chat && tabValue === 0) {
        const formatted = formatChat(data.chat);
        const exists = prev?.some((chat) => chat._id === data.chat._id);

        if (!exists) {
          return [formatted, ...prev];
        }

        return prev?.map?.((chat) =>
          chat._id === data.chat._id ? { ...chat, ...formatted } : chat
        );
      }

      return (
        prev?.map?.((chat) => {
          if (chat._id === data?.message_add?.chat_id) {
            return {
              ...chat,
              lastMessage: data?.message_add?.message_content,
              lastMessageTime: moment(
                data?.message_add?.message_date_time
              ).format("DD-MM-YYYY hh:mm A"),
            };
          }
          return chat;
        }) || []
      );
    });

    setNewNotification((prev) => {
      return [data.notification, ...(prev || [])];
    });

    const IsInChat =
      selectChatRef.current &&
      selectChatRef.current._id === data?.message_add?.chat_id;

    if (!IsInChat) {
      console.log("plus");
      setUnreadMessageCount((prev) => prev + 1);
    } else {
      console.log("plus");
      handleIncomingMessage(data);
    }
  };

  /**
   * Handles event when a user comes online.
   * Updates the chat list to set the user's online status to true.
   */
  const handleUserOnline = (data) => {
    // console.log("User online", data);
    setChatList((prev) => updateUserOnlineStatus(prev, data.user_id, true));
  };

  /**
   * Handles event when a user goes offline.
   * Updates the chat list to set the user's online status to false.
   */
  const handleUserOffline = (data) => {
    // console.log("User offline", data);
    setChatList((prev) => updateUserOnlineStatus(prev, data.user_id, false));
  };

  useEffect(() => {
    console.log("Unread message count:", unreadMessageCount);
  }, [unreadMessageCount, setUnreadMessageCount]);

  useEffect(() => {
    if (!socket) return;
    setSendMessagesLoader(false);

    socket.on("user_online", handleUserOnline);
    socket.on("user_offline", handleUserOffline);
    socket.on("end_chat_event_for_sender", handleEndChatSender);
    socket.on("end_chat_receiver", handleEndChatReceiver);
    socket.on("delete_chat_message_receiver", handleDeleteMessageReceiver);
    socket.on(
      "delete_chat_message_event_for_sender",
      handleDeleteMessageSender
    );
    socket.on("update_chat_message_receiver", handleUpdateMessageReceiver);
    socket.on(
      "update_chat_message_event_for_sender",
      handleUpdateMessageSender
    );
    socket.on("send_chat_message_event_for_sender", handleSenderEvent);
    socket.on("send_chat_message_receiver", handleReceiverEvent);

    return () => {
      socket.off("user_online", handleUserOnline);
      socket.off("user_offline", handleUserOffline);
      socket.off("delete_chat_message_receiver", handleDeleteMessageReceiver);
      socket.off(
        "delete_chat_message_event_for_sender",
        handleDeleteMessageSender
      );
      socket.off("update_chat_message_receiver", handleUpdateMessageReceiver);
      socket.off(
        "update_chat_message_event_for_sender",
        handleUpdateMessageSender
      );
      socket.off("end_chat_event_for_sender", handleEndChatSender);
      socket.off("end_chat_receiver", handleEndChatReceiver);
      socket.off("send_chat_message_event_for_sender", handleSenderEvent);
      socket.off("send_chat_message_receiver", handleReceiverEvent);
    };
  }, [socket]);
  return (
    <>
      {open && (
        <LiveChat
          open={open}
          handleClose={handleClose}
          isChatSelected={isChatSelected}
          setIsChatSelected={setIsChatSelected}
          setIsChatInitiated={setIsChatInitiated}
          isChatInitiated={isChatInitiated}
          selectChat={selectChat}
          setMessageId={setMessageId}
          messageId={messageId}
          setSelectChat={setSelectChat}
          sendMessagesLoader={sendMessagesLoader}
          setSendMessagesLoader={setSendMessagesLoader}
          delLoading={delLoading}
          setDelLoading={setDelLoading}
          tabValue={tabValue}
          setChatList={setChatList}
          setUserList={setUserList}
          setEndChat={setEndChat}
          setChats={setChats}
          userList={userList}
          chatList={chatList}
          setTabValue={setTabValue}
          openDelete={openDelete}
          setOpenDelete={setOpenDelete}
        />
      )}
    </>
  );
};
